/**
 * What stops a product being deleted.
 *
 * `roof_template_items.product_id` is ON DELETE RESTRICT, so a product any roof
 * template orders cannot be removed from the catalogue — the database refuses
 * it (migration 20260802140000). Postgres says so in the language of constraint
 * names, which is no use to the person holding the mouse, so this turns it into
 * the two things they need: which templates are in the way, and what to do
 * instead.
 *
 * The wording lives here rather than in the component so it is pinned by a
 * test. Naming the templates matters more than counting them; "4 templates" is
 * not something anybody can check, and "26 Standing Seam Snaplock" is.
 */

/** How many template lines to name before falling back to a count. */
const NAMES_SHOWN = 6;

/**
 * Why this product cannot be deleted, or null when nothing is in the way and
 * the plain confirmation will do.
 */
export function productDeletionWarning(
  productName: string,
  templateNames: readonly string[],
): string | null {
  if (templateNames.length === 0) return null;

  const unique = [...new Set(templateNames)].sort();
  const shown = unique.slice(0, NAMES_SHOWN);
  const rest = unique.length - shown.length;

  const list = shown.map((name) => `  • ${name}`).join("\n");
  const andMore = rest > 0 ? `\n  • …and ${rest} more` : "";

  const templateCount =
    unique.length === 1 ? "1 roof template" : `${unique.length} roof templates`;

  return (
    `"${productName}" is on ${templateCount}:\n\n${list}${andMore}\n\n` +
    `It cannot be deleted while they use it. Take it off those templates ` +
    `first, or set it to inactive to stop offering it without changing ` +
    `anything else.`
  );
}
